import { useMemo, useState } from 'react';
import { Pressable, RefreshControl, Text, View } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { ActivityIndicator } from 'react-native-paper';
import { useRouter } from 'expo-router';
import { LegendList } from '@legendapp/list';
import Chip from '@/components/General/Chip';
import EmptyListPlaceholder from '@/components/General/EmptyListPlaceholder';
import Unicon from '@/components/General/Unicon';
import SolidButton from '@/components/General/SolidButton';
import SheetSelector from '@/components/General/inputs/SheetSelector';
import SelectInput from '@/components/General/inputs/SelectInput';
import FollowUpHeader from '@/components/FollowUp/FollowUpHeader';
import AttentionReasonChips from '@/components/FollowUp/AttentionReasonChips';
import { Query } from '@/utils/react-query/Queries';
import useOrganizationEntitlements from '@/utils/hooks/useOrganizationEntitlements';
import { canViewModule, isFreeOrg } from '@/utils/organizationEntitlements';
import { extractFollowUpCode } from '@/data/followUp';
import { parseTimeFrame } from '@/utils/dater';
import {
    FOLLOW_UP_ATTENTION_REASONS,
    FOLLOW_UP_MONITORING_MODES,
    getMonitoringModeLabel,
} from '@/utils/constants/followUp';

/**
 * /follow-up/people — the monitored People list (spec §6.1 People): a focused
 * view of monitored people, server-side filtered + cursor paginated. Cards
 * show caregiver, last attendance/attempt/connected and attention reasons —
 * no private narrative in list previews (§8.6).
 */

const FilterToggleChip = ({ label, icon, active, onPress, testID }) => (
    <Pressable
        testID={testID}
        accessibilityRole="button"
        accessibilityState={{ selected: active }}
        onPress={onPress}
        style={[styles.filterChip, active && styles.filterChipActive]}
    >
        <Unicon name={icon} size={13} colorVariant={active ? 'primary' : 'textSecondary'} />
        <Text style={[styles.filterChipText, active && styles.filterChipTextActive]}>{label}</Text>
    </Pressable>
);

const PersonCard = ({ item, onPress }) => (
    <Pressable
        testID={`follow-up-people-${item.personID}`}
        accessibilityRole="button"
        onPress={onPress}
        style={styles.card}
    >
        <View style={styles.cardTop}>
            <View style={styles.nameWrap}>
                <Text style={styles.name} numberOfLines={1}>{item.name || 'Unknown'}</Text>
                {!!item.hasContactWarning && (
                    <Unicon name="hand-left-outline" size={14} colorVariant="error" />
                )}
            </View>
            {(item.openTaskCount || 0) > 0 && (
                <Chip icon="clipboard-outline" colorVariant="textSecondary">
                    {`${item.openTaskCount} open`}
                </Chip>
            )}
            {item.monitoringMode && item.monitoringMode !== 'AUTO' && (
                <Chip
                    icon={item.monitoringMode === 'EXCLUDE' ? 'remove-circle-outline' : 'add-circle-outline'}
                    colorVariant={item.monitoringMode === 'EXCLUDE' ? 'warning' : 'success'}
                >
                    {getMonitoringModeLabel(item.monitoringMode)}
                </Chip>
            )}
        </View>

        <Text style={styles.metaLine} numberOfLines={1}>
                        {`Connector: ${item.caregiverName || 'Unassigned'}`}
        </Text>
        <Text style={styles.metaLine} numberOfLines={2}>
            {`Attendance: ${item.lastAttendanceAt ? parseTimeFrame(item.lastAttendanceAt) : 'No recorded qualifying attendance'}`}
            {` · Attempt: ${item.lastAttemptAt ? parseTimeFrame(item.lastAttemptAt) : 'No contact recorded'}`}
            {` · Connected: ${item.lastConnectedAt ? parseTimeFrame(item.lastConnectedAt) : '—'}`}
        </Text>

        <AttentionReasonChips reasons={item.attentionReasons} testIDPrefix={`people-attention-${item.personID}`} />
    </Pressable>
);

export default function FollowUpPeopleScreen() {
    const router = useRouter();
    const { data: loggedInUser } = Query.people.loggedInUser();
    const { entitlements } = useOrganizationEntitlements();

    const organizationID = loggedInUser?.organizationID;
    const canView = !!loggedInUser?.can?.('View Follow Up');
    const canManage = !!loggedInUser?.can?.('Manage Follow Up');
    const moduleAllowed = canViewModule(entitlements, 'paths');
    const gated = !!loggedInUser && canView && moduleAllowed;

    // Server-side filters (follow_up_list_people p_payload.filter).
    const [caregiver, setCaregiver] = useState(null);
    const [unassigned, setUnassigned] = useState(false);
    const [reasonFilter, setReasonFilter] = useState(null);
    const [modeFilter, setModeFilter] = useState(null);
    const [warningOnly, setWarningOnly] = useState(false);

    const filter = useMemo(() => {
        const f = {};
        if (unassigned) f.unassigned = true;
        else if (caregiver?.id) f.caregiverID = caregiver.id;
        if (reasonFilter) f.attentionReason = reasonFilter;
        if (modeFilter) f.monitoringMode = modeFilter;
        if (warningOnly) f.hasWarning = true;
        return f;
    }, [caregiver, unassigned, reasonFilter, modeFilter, warningOnly]);

    const hasFilters = Object.keys(filter).length > 0;

    const {
        data, isLoading, isError, error, isRefetching, refetch,
        hasNextPage, fetchNextPage, isFetchingNextPage,
    } = Query.followUp.people({
        organizationID,
        filter: hasFilters ? filter : null,
        options: { enabled: gated },
    });

    const people = useMemo(
        () => (data?.pages || []).flatMap((page) => page?.people || []),
        [data]
    );

    if (loggedInUser && (!canView || !moduleAllowed)) {
        return (
            <View style={styles.deniedWrap}>
                <EmptyListPlaceholder
                    type="404"
                    spacing={40}
                    message={!moduleAllowed && isFreeOrg(entitlements)
                        ? 'Follow Up is not available on this plan'
                        : "You don't have permission to view Follow Up. Contact your Reach administrator to be added to the Follow Up team."}
                />
            </View>
        );
    }

    const reasonOptions = [
        { value: null, label: 'Any reason' },
        ...FOLLOW_UP_ATTENTION_REASONS.map((r) => ({ value: r.value, label: r.label })),
    ];
    const modeOptions = [
        { value: null, label: 'Any monitoring' },
        ...FOLLOW_UP_MONITORING_MODES,
    ];

    return (
        <View style={styles.container}>
            <View style={styles.content}>
                <FollowUpHeader active="people" canManage={canManage} />

                <View style={styles.filterRow}>
                    <View style={styles.filterSelectWrap}>
                        <SelectInput
                            placeholder="Any reason"
                            value={reasonFilter}
                            options={reasonOptions}
                            hideClearButton
                            hideSelectedPills
                            onUpdate={(v) => setReasonFilter(v || null)}
                        />
                    </View>
                    <View style={styles.filterSelectWrap}>
                        <SelectInput
                            placeholder="Any monitoring"
                            value={modeFilter}
                            options={modeOptions}
                            hideClearButton
                            hideSelectedPills
                            onUpdate={(v) => setModeFilter(v || null)}
                        />
                    </View>
                </View>
                <View style={styles.filterRow}>
                    <SheetSelector
                        type="people"
                        title="Filter by connector"
                        titleOnly
                        placeholder={caregiver ? `${caregiver.firstName || ''} ${caregiver.lastName || ''}`.trim() : 'Connector: Any'}
                        value={caregiver}
                        onChange={(person) => {
                            // Single mode can yield null/an array on clear.
                            const p = person && !Array.isArray(person) ? person : null;
                            setCaregiver(p);
                            if (p?.id) setUnassigned(false);
                        }}
                        testID="people-filter-caregiver"
                    />
                    <FilterToggleChip
                        testID="people-filter-unassigned"
                        label="Unassigned"
                        icon="person-add-outline"
                        active={unassigned}
                        onPress={() => {
                            setUnassigned((v) => {
                                if (!v) setCaregiver(null);
                                return !v;
                            });
                        }}
                    />
                    <FilterToggleChip
                        testID="people-filter-warning"
                        label="No Contact warning"
                        icon="hand-left-outline"
                        active={warningOnly}
                        onPress={() => setWarningOnly((v) => !v)}
                    />
                    {hasFilters && (
                        <Pressable
                            testID="people-filter-clear"
                            accessibilityRole="button"
                            onPress={() => {
                                setCaregiver(null);
                                setUnassigned(false);
                                setReasonFilter(null);
                                setModeFilter(null);
                                setWarningOnly(false);
                            }}
                            style={styles.clearFilters}
                        >
                            <Text style={styles.clearFiltersText}>Clear filters</Text>
                        </Pressable>
                    )}
                </View>

                {isError ? (
                    <View style={styles.errorWrap}>
                        <Text style={styles.errorText}>
                            {(extractFollowUpCode(error) || error?.followUpCode) === 'FOLLOW_UP_DENIED'
                                ? "You don't have permission to view monitored people."
                                : 'Something went wrong loading the people list. Pull to refresh to try again.'}
                        </Text>
                    </View>
                ) : (
                    <LegendList
                        recycleItems
                        keyExtractor={(item) => item.personID}
                        estimatedItemSize={120}
                        data={isLoading ? [] : people}
                        renderItem={({ item }) => (
                            <PersonCard
                                item={item}
                                onPress={() => router.push(`/people/${item.personID}?tab=followup`)}
                            />
                        )}
                        contentContainerStyle={styles.listContent}
                        refreshControl={
                            <RefreshControl
                                refreshing={isRefetching && !isFetchingNextPage}
                                onRefresh={refetch}
                            />
                        }
                        ListEmptyComponent={
                            isLoading ? (
                                <View style={styles.loadingWrap}><ActivityIndicator size="large" /></View>
                            ) : (
                                <EmptyListPlaceholder
                                    spacing={30}
                                    icon="people-outline"
                                    message={hasFilters
                                        ? 'No monitored people match these filters.'
                                        : 'No one is being monitored yet. Audiences and manual includes control monitoring.'}
                                />
                            )
                        }
                        ListFooterComponent={
                            hasNextPage ? (
                                <View style={styles.footer}>
                                    {isFetchingNextPage ? (
                                        <ActivityIndicator />
                                    ) : (
                                        <SolidButton
                                            testID="people-load-more"
                                            variant="outlined"
                                            type="secondary"
                                            text="Load More"
                                            onPress={() => fetchNextPage()}
                                        />
                                    )}
                                </View>
                            ) : null
                        }
                        keyboardShouldPersistTaps="handled"
                    />
                )}
            </View>
        </View>
    );
}

const styles = StyleSheet.create(({ colors, fonts, shadows }) => ({
    container: { flex: 1 },
    content: { flex: 1, padding: 16, gap: 12, maxWidth: 1180, width: '100%', alignSelf: 'center' },
    deniedWrap: { flex: 1, justifyContent: 'center' },
    header: { paddingHorizontal: 4, paddingTop: 4 },
    headerTitle: { fontSize: 20, fontFamily: fonts.heading, color: colors.text },
    headerSubtitle: { fontSize: 12, color: colors.textSecondary, marginTop: 2 },
    filterRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, alignItems: 'center' },
    filterSelectWrap: { minWidth: 160, flexGrow: 1, maxWidth: 260 },
    filterChip: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        paddingHorizontal: 12,
        paddingVertical: 8,
        borderRadius: 16,
        backgroundColor: colors.surface,
        borderWidth: 1,
        borderColor: colors.border,
        _web: { cursor: 'pointer' },
    },
    filterChipActive: {
        backgroundColor: colors.primaryContainer,
        borderColor: colors.primary,
    },
    filterChipText: {
        fontSize: 13,
        color: colors.textSecondary,
        fontFamily: fonts.medium,
    },
    filterChipTextActive: {
        color: colors.primary,
        fontFamily: fonts.bold,
    },
    clearFilters: { paddingHorizontal: 8, paddingVertical: 8, _web: { cursor: 'pointer' } },
    clearFiltersText: { fontSize: 13, color: colors.primary, fontFamily: fonts.medium },
    listContent: { paddingBottom: 60 },
    loadingWrap: { padding: 40, alignItems: 'center' },
    errorWrap: { padding: 24 },
    errorText: { fontSize: 13, color: colors.textSecondary, textAlign: 'center', lineHeight: 19 },
    footer: { alignItems: 'center', paddingVertical: 16 },
    card: {
        backgroundColor: colors.surface,
        borderRadius: 12,
        padding: 12,
        marginBottom: 8,
        gap: 6,
        boxShadow: shadows.card,
        _web: {
            cursor: 'pointer',
            transition: 'all 0.15s ease-in-out',
            _hover: { backgroundColor: colors.hover },
        },
    },
    cardTop: { flexDirection: 'row', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
    nameWrap: { flex: 1, flexDirection: 'row', alignItems: 'center', gap: 6, minWidth: 0 },
    name: { fontSize: 15, fontFamily: fonts.heading, color: colors.text },
    metaLine: { fontSize: 12, color: colors.textSecondary },
}));
